import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MAINTENANCE_EVENT } from '@koob/utils';
import { icons } from '../icons';
import KoobLoader from './KoobLoader';
import KoobLogo from './KoobLogo';

type MaintenanceMode = 'off' | 'read-only' | 'closed';

type MaintenanceState = {
  mode: MaintenanceMode;
  message: string | null;
  until: string | null;
};

const OFF: MaintenanceState = { mode: 'off', message: null, until: null };
const POLL_MS = 60_000;
// Faster while in maintenance, so users get back in soon after it ends.
const POLL_IN_MAINTENANCE_MS = 30_000;
// The first answer holds the app back (no request goes out while closed);
// an API slow to answer must not hold it longer than this.
const FIRST_CHECK_TIMEOUT_MS = 3_000;

// The apps' bundlers replace process.env.NODE_ENV; packages/ui has no Node types.
declare const process: { env: { NODE_ENV?: string } };

// Development builds show the closed page on /maintenance, to preview it;
// ?message= sets its message. Production builds never do.
const previewState = (): MaintenanceState | null => {
  if (process.env.NODE_ENV === 'production') return null;
  if (window.location.pathname !== '/maintenance') return null;
  return {
    mode: 'closed',
    message: new URLSearchParams(window.location.search).get('message'),
    until: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
  };
};

const fetchState = async (apiBase: string): Promise<MaintenanceState> => {
  // No token on purpose: the route is public, and authenticating reads the
  // database closed mode may be keeping shut.
  const res = await fetch(`${apiBase}/api/internal/maintenance`, {
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`maintenance status ${res.status}`);
  return res.json();
};

const useUntilLabel = (until: string | null) => {
  const { i18n } = useTranslation();
  if (!until) return null;
  return new Date(until).toLocaleString(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

function MaintenancePage({ state }: { state: MaintenanceState }) {
  const { t } = useTranslation('maintenance');
  const until = useUntilLabel(state.until);

  return (
    <div className="flex min-h-screen flex-col bg-k-h text-white">
      <header className="px-10 py-7">
        <KoobLogo reverse className="h-7 w-auto" />
      </header>
      <main className="flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-24 pt-10 text-center">
        <KoobLoader size={120} onDark />
        <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-k-orange">
          {t('overline')}
        </p>
        <h1 className="m-0 max-w-[560px] text-[40px] font-bold leading-[48px] tracking-[-0.02em]">
          {t('closedTitle')}
        </h1>
        <p className="m-0 max-w-[520px] text-base leading-[26px] text-navy-200">
          {t('closedDescription')}
        </p>

        {state.message && (
          <p className="m-0 w-full max-w-[520px] rounded-xl bg-navy-700 px-5 py-4 text-left text-sm leading-relaxed text-white">
            {state.message}
          </p>
        )}

        {until && (
          <p className="m-0 inline-flex items-center gap-2 rounded-full border border-solid border-navy-500 px-4 py-1.5 text-sm font-medium">
            <i className={`${icons.clock} text-navy-200`} />
            {t('until', { date: until })}
          </p>
        )}
      </main>
    </div>
  );
}

function MaintenanceBanner({
  state,
  onClose,
}: {
  state: MaintenanceState;
  onClose: () => void;
}) {
  const { t } = useTranslation('maintenance');
  const until = useUntilLabel(state.until);

  return (
    <div
      role="status"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-floating flex items-start gap-3 rounded-lg border border-solid border-amber-600/20 bg-amber-50 p-4 text-sm text-amber-800 shadow-lg"
    >
      <i className={`${icons.warning} leading-5`} />
      <div className="flex-1 space-y-1">
        <p className="font-semibold">{t('readOnlyTitle')}</p>
        <p>{t('readOnlyDescription')}</p>
        {state.message && <p>{state.message}</p>}
        {until && <p>{t('until', { date: until })}</p>}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('hide')}
        className="border-none bg-transparent cursor-pointer text-amber-700 hover:text-amber-900"
      >
        <i className={icons.close} />
      </button>
    </div>
  );
}

/**
 * Wraps a whole app. Read-only maintenance: a banner over the app. Closed: a
 * full page instead of the app, which reloads once maintenance is over. Reads
 * the API's status on start, every minute (30 s while in maintenance), and at
 * once when a request comes back 503 MAINTENANCE (reportMaintenance in each
 * app's axios interceptor). An unreachable API is not maintenance: the app
 * shows its own errors.
 */
export default function KoobMaintenanceGate({
  apiBase,
  children,
}: {
  apiBase: string;
  children: ReactNode;
}) {
  const [state, setState] = useState<MaintenanceState | null>(null);
  const [hidden, setHidden] = useState(false);
  const modeRef = useRef<MaintenanceMode | null>(null);

  const check = useCallback(
    () =>
      fetchState(apiBase)
        .then((next) => {
          if (modeRef.current === 'closed' && next.mode !== 'closed') {
            window.location.reload();
            return;
          }
          if (modeRef.current !== next.mode) setHidden(false);
          modeRef.current = next.mode;
          setState(next);
        })
        // Keep what we had; the first check falls back to the app.
        .catch(() => setState((previous) => previous ?? OFF)),
    [apiBase],
  );

  useEffect(() => {
    check();
    const timer = setTimeout(
      () => setState((previous) => previous ?? OFF),
      FIRST_CHECK_TIMEOUT_MS,
    );
    window.addEventListener(MAINTENANCE_EVENT, check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(MAINTENANCE_EVENT, check);
    };
  }, [check]);

  const mode = state?.mode;
  useEffect(() => {
    if (!mode) return undefined;
    const timer = setInterval(
      check,
      mode === 'off' ? POLL_MS : POLL_IN_MAINTENANCE_MS,
    );
    return () => clearInterval(timer);
  }, [check, mode]);

  const preview = previewState();
  if (preview) return <MaintenancePage state={preview} />;
  if (!state) return null;
  if (state.mode === 'closed') return <MaintenancePage state={state} />;

  return (
    <>
      {children}
      {state.mode === 'read-only' && !hidden && (
        <MaintenanceBanner state={state} onClose={() => setHidden(true)} />
      )}
    </>
  );
}
